import { Button, Textarea } from '@wtfalch/design';
import { useState } from 'react';

/** Matrix caps an event at 64 KiB and an edit carries the text twice (the
 *  fallback `body` and `m.new_content`), so an edit stays well under it. */
export const MAX_EDIT_LENGTH = 4000;

/** What an edit may be saved as: the text trimmed, or why it cannot be saved.
 *  The text comes from a textarea, so it is untrusted until it passes here. */
export function checkEdit(raw: string, original: string): { text: string } | { problem: string } {
  const text = raw.trim();
  if (!text) return { problem: 'A message cannot be empty. Delete it instead.' };
  if (text.length > MAX_EDIT_LENGTH) {
    return { problem: `Keep it under ${MAX_EDIT_LENGTH} characters.` };
  }
  if (text === original.trim()) return { problem: 'Nothing has changed.' };
  return { text };
}

/** A message's text, in place, while its author edits it. Enter saves,
 *  Shift+Enter breaks a line, Escape leaves the message as it was. */
export function MessageEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial: string;
  onSave: (text: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const checked = checkEdit(value, initial);

  async function save() {
    if ('problem' in checked || busy) return;
    setBusy(true);
    setFailed(null);
    try {
      await onSave(checked.text);
    } catch {
      setFailed("Couldn't save the edit. Try again.");
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2 chat-edit">
      <Textarea
        autoFocus
        rows={2}
        aria-label="Edit message"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.nativeEvent.isComposing) return;
          if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          } else if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void save();
          }
        }}
      />
      <div className="flex items-center gap-2">
        <Button size="sm" kind="primary" disabled={'problem' in checked || busy} onPress={save}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
        <Button size="sm" kind="ghost" onPress={onCancel}>
          Cancel
        </Button>
        <span
          className={`text-xs chat-edit-note ${failed ? 'text-bad' : 'text-muted'}`}
          role="status"
        >
          {failed ?? ('problem' in checked ? checked.problem : '')}
        </span>
      </div>
    </div>
  );
}
